import {useEffect,useRef,useState} from 'react';
import {Link} from 'react-router-dom';
import {Check} from 'lucide-react';
import {authDestination} from './authContinuation';
import {InstallDisclosure} from './InstallDisclosure';
import {ConnectionScope,PlanChoice} from './PlanChoice';
import {RemoteConnectorLink,RemoteInitPrompt} from './RemoteConnectionDetails';
import {activeRemoteInstallation,clientSetupRequest,connectRemoteInstallation,forgetRemoteInstallation,readRemoteInstallation} from './remoteInstallation';

export function RemoteClientEntry({host}) {
  const signedIn=!!localStorage.getItem('token');
  const returnTo=`/brief?client=${host.id}`;
  return <div className="pabs-install-remote">
    <p>{host.label} runs in the browser, so there’s nothing to install. Create a private link in Pabs, add it to {host.label}, then ask it to set up your project.</p>
    {signedIn ? <Link className="pabs-install-next" to={returnTo}>Continue setup</Link> : <p><Link to={authDestination('/register',returnTo)}>Create a Pabs account</Link> or <Link to={authDestination('/login',returnTo)}>sign in</Link> to get started.</p>}
  </div>;
}

// Signed-in setup on /brief: create the link, add it to the tool, run setup.
function RemoteSetupSteps({host,planId=null}) {
  const returnTo=`/brief?client=${host.id}`;
  // Opened from a plan in Pabs: start on that plan when it is the person's.
  const suggestedPlan=planId?String(planId):null;
  const [plans,setPlans]=useState(null);
  const [selection,setSelection]=useState('new');
  const [connection,setConnection]=useState(null);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState('');
  // Keeps steps 2 and 3 filled while they collapse after "Start again".
  const shown=useRef(null);
  if(connection) shown.current=connection;
  useEffect(()=>{
    let cancelled=false;
    clientSetupRequest('plans',{},returnTo).then(async response=>{
      if(!response) return;
      const value=await response.json().catch(()=>({}));
      if(cancelled) return;
      const list=response.ok ? value.plans || [] : [];
      setPlans(list);
      if(suggestedPlan && list.some(plan=>String(plan.id)===suggestedPlan)) setSelection(suggestedPlan);
    }).catch(()=>{if(!cancelled) setPlans([]);});
    // Pick up a link this tab already created, for example after a reload.
    const saved=activeRemoteInstallation(host.id);
    if(saved) clientSetupRequest(saved.id,{},returnTo).then(async response=>{
      if(!response || cancelled) return;
      const value=await response.json().catch(()=>({}));
      if(response.ok && ['authorized','connected'].includes(value.status)) setConnection({id:saved.id,status:value.status,plan_id:value.plan_id,project_label:value.project_label});
      else forgetRemoteInstallation(saved.id);
    }).catch(()=>{});
    return()=>{cancelled=true;};
  },[host.id,returnTo,suggestedPlan]);
  async function create() {
    setBusy(true);setError('');
    try {
      const result=await connectRemoteInstallation(host.id,selection==='new'?null:selection);
      if(result) setConnection(result);
    } catch(problem) {setError(problem.message);}
    finally {setBusy(false);}
  }
  async function startAgain() {
    setBusy(true);setError('');
    try {
      // Revoke the old link so an approved, unused one can't linger.
      const response=await clientSetupRequest(`${connection.id}/revoke`,{method:'POST'},returnTo);
      if(!response) return;
      if(!response.ok && response.status!==404) throw new Error('Couldn’t revoke the old link. Try again.');
      forgetRemoteInstallation(connection.id);
      setConnection(null);
    } catch(problem) {setError(problem.message);}
    finally {setBusy(false);}
  }
  const ready=!!connection;
  const visible=connection || shown.current;
  const secret=visible && readRemoteInstallation(visible.id)?.secret;
  return <div className="pabs-brief-remote">
    <ol className="pabs-brief-steps">
      <li className="pabs-brief-step" data-state={ready?'done':'current'}>
        <span className="pabs-brief-step-number" aria-hidden="true">{ready?<Check size={15} strokeWidth={3}/>:'1'}</span>
        <div className="pabs-brief-step-body">
          <h3>{ready?'Your connection link is ready':'Create your connection link'}</h3>
          <InstallDisclosure open={!ready}>
            <div className="pabs-brief-step-content">
              <div className="pabs-brief-link-row">
                {plans?.length>0 && <PlanChoice plans={plans} value={selection} onChange={setSelection}/>}
                <button type="button" className="pabs-install-create-link" onClick={create} disabled={busy || plans===null}>{busy?'Creating link…':'Create connection link'}</button>
              </div>
              <p className="pabs-brief-note"><ConnectionScope selection={selection}/></p>
            </div>
          </InstallDisclosure>
          <InstallDisclosure open={ready}>
            <p className="pabs-brief-step-summary">{visible?.plan_id?`For ${visible.project_label}.`:'For a new product.'} <button type="button" className="pabs-brief-link" onClick={startAgain} disabled={busy}>Start again</button></p>
          </InstallDisclosure>
          {error && <p role="alert" className="pabs-brief-error">{error}</p>}
        </div>
      </li>
      <li className="pabs-brief-step" data-state={ready?'current':'upcoming'}>
        <span className="pabs-brief-step-number" aria-hidden="true">2</span>
        <div className="pabs-brief-step-body">
          <h3>Add it to {host.label} as a connector</h3>
          <InstallDisclosure open={ready}>
            <div className="pabs-brief-step-content">{secret && <RemoteConnectorLink host={host} secret={secret}/>}</div>
          </InstallDisclosure>
        </div>
      </li>
      <li className="pabs-brief-step" data-state={ready?'current':'upcoming'}>
        <span className="pabs-brief-step-number" aria-hidden="true">3</span>
        <div className="pabs-brief-step-body">
          <h3>Back in your {host.label} chat, ask it to set up your project</h3>
          <InstallDisclosure open={ready}>
            <div className="pabs-brief-step-content"><RemoteInitPrompt planId={visible?.plan_id}/></div>
          </InstallDisclosure>
        </div>
      </li>
    </ol>
    <p className="pabs-brief-note"><a href={host.docs} target="_blank" rel="noreferrer">{host.label} connector help</a></p>
  </div>;
}

export default function RemoteClientSetup({host,planId=null}) {
  return localStorage.getItem('token') ? <RemoteSetupSteps host={host} planId={planId}/> : <RemoteClientEntry host={host}/>;
}
